/**
 * SIM / ICCID helpers for station detail.
 *
 * Backend (tcp-server / tcp-processing) maps providers via ICCID prefix and stores
 * simCardProvider + realSimCardId (padding F stripped). UI still has a fallback
 * for older UNKNOWN rows until backfill / next ICCID poll.
 */

/** Longer prefixes first. len = normalized ICCID length used by tcp (best-effort). */
const ICCID_PROVIDER_RULES = [
  // Russia — big three (same as tcp SimCardProvider)
  { prefix: '8970101', code: 'MTS', len: 20 },
  { prefix: '8970102', code: 'MEGAFON', len: 17 },
  { prefix: '8970199', code: 'BEELINE', len: 18 },
  // Russia — Volna (КТК Телеком) + observed 8970160 batch
  { prefix: '8970137', code: 'VOLNA', len: 20 },
  { prefix: '8970132', code: 'VOLNA', len: 20 },
  { prefix: '8970131', code: 'VOLNA', len: 20 },
  { prefix: '8970160', code: 'TELE2_RU', len: 20 },
  // Belarus (89 + 375) — NOT 89257 (that was a wrong FE guess)
  { prefix: '89375012', code: 'A1_BY', len: 20 },
  { prefix: '8937501', code: 'A1_BY', len: 20 },
  { prefix: '8937502', code: 'MTS_BY', len: 20 },
  { prefix: '8937504', code: 'LIFE_BY', len: 20 },
  { prefix: '89375', code: 'BELARUS', len: 20 },
  // Indonesia (62)
  { prefix: '896214', code: 'INDOSAT', len: 20 },
  { prefix: '896216', code: 'XL_AXIATA', len: 20 },
  { prefix: '896209', code: 'TRI_ID', len: 20 },
  { prefix: '89621', code: 'TELKOMSEL', len: 20 },
  { prefix: '8962', code: 'INDONESIA', len: 20 },
  // Estonia (372)
  { prefix: '893720', code: 'ELISA_EE', len: 20 },
  { prefix: '893722', code: 'TELIA_EE', len: 20 },
  { prefix: '893725', code: 'TELE2_EE', len: 20 },
  { prefix: '894372', code: 'ESTONIA', len: 20 },
  { prefix: '89372', code: 'ESTONIA', len: 20 },
];

const PROVIDER_LABEL_KEYS = {
  MTS: 'mts',
  MEGAFON: 'megafon',
  BEELINE: 'beeline',
  VOLNA: 'volna',
  TELE2_RU: 'tele2Ru',
  A1_BY: 'a1By',
  MTS_BY: 'mtsBy',
  LIFE_BY: 'lifeBy',
  BELARUS: 'belarus',
  TELKOMSEL: 'telkomsel',
  INDOSAT: 'indosat',
  XL_AXIATA: 'xlAxiata',
  TRI_ID: 'triId',
  INDONESIA: 'indonesia',
  ELISA_EE: 'elisaEe',
  TELIA_EE: 'teliaEe',
  TELE2_EE: 'tele2Ee',
  ESTONIA: 'estonia',
  UNKNOWN: 'unknown',
};

function asStr(value) {
  if (value == null) return '';
  return String(value).trim();
}

/** Strip trailing F/f padding often present in raw station ICCID dumps. */
export function stripIccidPadding(value) {
  const s = asStr(value);
  if (!s) return '';
  return s.replace(/[fF]+$/g, '').replace(/\s+/g, '');
}

export function pickRawSimId(station) {
  if (!station || typeof station !== 'object') return '';
  const nested = station.station || {};
  return (
    asStr(station.simCardId) ||
    asStr(station.rawIccid) ||
    asStr(nested.simCardId) ||
    asStr(nested.rawIccid) ||
    ''
  );
}

export function pickNormalizedIccid(station) {
  if (!station || typeof station !== 'object') return '';
  const nested = station.station || {};
  const explicit =
    asStr(station.realSimCardId) ||
    asStr(nested.realSimCardId) ||
    '';
  if (explicit) return stripIccidPadding(explicit);

  const raw = pickRawSimId(station);
  const cleaned = stripIccidPadding(raw);
  if (!cleaned) return '';

  const rule = matchProviderRule(cleaned);
  if (rule?.len && cleaned.length >= rule.len) {
    return cleaned.slice(0, rule.len);
  }
  return cleaned;
}

function matchProviderRule(iccidDigits) {
  const id = stripIccidPadding(iccidDigits);
  if (!id) return null;
  return ICCID_PROVIDER_RULES.find((r) => id.startsWith(r.prefix)) || null;
}

/**
 * Resolve provider code for display.
 * Prefers a non-UNKNOWN API value; otherwise detects from ICCID.
 */
export function resolveSimProviderCode(station) {
  if (!station || typeof station !== 'object') return 'UNKNOWN';
  const nested = station.station || {};
  const apiRaw = asStr(station.simCardProvider || nested.simCardProvider).toUpperCase();
  const api = apiRaw.replace(/\s+/g, '_');

  const iccid = pickNormalizedIccid(station) || stripIccidPadding(pickRawSimId(station));
  const detected = matchProviderRule(iccid)?.code || null;

  if (api && api !== 'UNKNOWN') {
    // If API says big-three but ICCID points elsewhere, trust ICCID (stale provider).
    if (
      detected &&
      detected !== api &&
      ['MTS', 'MEGAFON', 'BEELINE'].includes(api) &&
      !['MTS', 'MEGAFON', 'BEELINE'].includes(detected)
    ) {
      return detected;
    }
    return api;
  }

  return detected || 'UNKNOWN';
}

/**
 * @param {function} t - i18n t()
 * @returns {string}
 */
export function formatSimProviderLabel(station, t) {
  const code = resolveSimProviderCode(station);
  const key = PROVIDER_LABEL_KEYS[code];
  if (key && typeof t === 'function') {
    return t(`drawer.station.simProviderName_${key}`, { defaultValue: humanizeCode(code) });
  }
  return humanizeCode(code);
}

function humanizeCode(code) {
  if (!code || code === 'UNKNOWN') return 'Unknown';
  return String(code)
    .split('_')
    .map((p) => p.charAt(0) + p.slice(1).toLowerCase())
    .join(' ');
}

/** Whether Connection & SIM block should render SIM rows. */
export function hasSimInfo(station) {
  return Boolean(
    pickNormalizedIccid(station) ||
    pickRawSimId(station) ||
    resolveSimProviderCode(station) !== 'UNKNOWN' ||
    asStr(station?.simCardProvider)
  );
}
